import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { ReactComponent as Logo } from "../assets/Logo.svg";
import LangSwitcher from "./langswitcher/LangSwitcher";

const BUSINESS_BUTTON_INTRO_DELAY_MS = 650;
const BUSINESS_BUTTON_INTRO_DURATION_MS = 3800;

const Navbar = ({ scrollThreshold = 20 }) => {
  const { t, i18n } = useTranslation();
  let navigate = useNavigate();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isBusinessButtonIntroActive, setIsBusinessButtonIntroActive] =
    useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > scrollThreshold);
  }, [scrollThreshold]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const startIntroTimeout = window.setTimeout(() => {
      setIsBusinessButtonIntroActive(true);
    }, BUSINESS_BUTTON_INTRO_DELAY_MS);

    return () => window.clearTimeout(startIntroTimeout);
  }, []);

  useEffect(() => {
    if (!isBusinessButtonIntroActive) return undefined;

    const introTimeout = window.setTimeout(() => {
      setIsBusinessButtonIntroActive(false);
    }, BUSINESS_BUTTON_INTRO_DURATION_MS);

    return () => window.clearTimeout(introTimeout);
  }, [isBusinessButtonIntroActive]);

  const handleBusinessNavigation = () => {
    const currentLanguage =
      i18n.resolvedLanguage === "he" || i18n.language === "he" ? "he" : "en";

    window.location.assign(`/business?lang=${currentLanguage}`);
  };

  return (
    <NavWrapper isScrolled={isScrolled}>
      <NavBackground isScrolled={isScrolled} />
      <NavContent>
        <MainNavContent>
          <LeftSection>
            <LogoWrapper onClick={() => navigate("/")}>
              <Logo alt="Nightly" />
            </LogoWrapper>
          </LeftSection>
          <RightSection>
            <Button
              $playIntro={isBusinessButtonIntroActive}
              onClick={handleBusinessNavigation}
            >
              <ButtonLabel>{t("hero_section.business_btn")}</ButtonLabel>
            </Button>
            <LangSwitcherWrapper>
              <LangSwitcher />
            </LangSwitcherWrapper>
          </RightSection>
        </MainNavContent>
      </NavContent>
    </NavWrapper>
  );
};

export default Navbar;

const NavWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 3;
  transition: background-color 0.3s ease-in-out;
  margin-top: -3rem;
  direction: ltr;

  @media (max-width: 768px) {
    padding-top: 0;
    margin-top: 3rem;
    margin-bottom: 3rem;
  }
`;

const NavBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ isScrolled }) =>
    isScrolled ? "rgba(28, 28, 23, 0.8)" : "transparent"};
  box-shadow: ${({ isScrolled }) =>
    isScrolled ? "0 1px 4px rgba(0, 0, 0, 0.1)" : "none"};
  border-bottom: ${({ isScrolled }) =>
    isScrolled ? "1px solid #48473a" : "none"};
  backdrop-filter: ${({ isScrolled }) =>
    isScrolled ? "blur(10px)" : "none"}; // Apply blur effect when scrolled
  z-index: -1;
  /* transition: background-color 0.4s ease-in-out, border-bottom 0.4s ease-in-out; */
  transition: background-color 0.4s ease-in-out, border-bottom 0.4s ease-in-out,
    backdrop-filter 0.4s ease-in-out;
  height: 75%;
  width: 150%;
  opacity: 0.99;
  @media (max-width: 768px) {
    top: unset; // Remove bottom positioning to avoid overlap issues
    height: 200%;
  }
`;

const NavContent = styled.nav`
  max-width: 1250px;
  margin: 0 auto;
  padding: 0 2rem 0;
`;

const MainNavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.7rem;
  margin-right: 0.1rem;
  margin-left: -1.5rem;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const LogoWrapper = styled.div`
  cursor: pointer;
  svg {
    height: 1.4rem;
    @media (max-width: 768px) {
      height: 1rem;
      cursor: pointer;
    }
  }
`;

const businessButtonGlow = keyframes`
  0%, 100% {
    border-color: #939182;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.05),
      0 0 0 rgba(233, 233, 40, 0),
      0 8px 18px rgba(0, 0, 0, 0.18);
  }

  50% {
    border-color: rgba(233, 233, 40, 0.52);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.08),
      0 0 22px rgba(233, 233, 40, 0.14),
      0 10px 22px rgba(0, 0, 0, 0.24);
  }
`;

const businessButtonSweep = keyframes`
  0% {
    transform: translateX(-155%);
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  58% {
    transform: translateX(155%);
    opacity: 0;
  }

  100% {
    transform: translateX(155%);
    opacity: 0;
  }
`;

const Button = styled.button`
  font-size: 1.3rem;
  min-width: 150px;
  min-height: 53px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.03));
  border: 2px solid #939182;
  color: #e9e928;
  border-radius: 2rem;
  cursor: pointer;
  transition: transform 0.22s ease-in, border-color 0.22s ease-in, color 0.22s ease-in,
    box-shadow 0.22s ease-in, background-color 0.22s ease-in;
  margin-right: 1rem;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 8px 18px rgba(0, 0, 0, 0.18);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.04),
      rgba(233, 233, 40, 0.08)
    );
    opacity: 0.85;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-155%);
    opacity: 0;
    background: linear-gradient(
      120deg,
      transparent 0%,
      transparent 30%,
      rgba(255, 255, 255, 0.08) 42%,
      rgba(233, 233, 40, 0.34) 50%,
      rgba(255, 255, 255, 0.08) 58%,
      transparent 70%,
      transparent 100%
    );
    z-index: 0;
  }

  &:focus-visible::after {
    animation: ${businessButtonSweep} 3s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:focus-visible {
    outline: none;
    border-color: rgba(233, 233, 40, 0.62);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.09),
      0 0 0 2px rgba(233, 233, 40, 0.18),
      0 0 22px rgba(233, 233, 40, 0.14),
      0 12px 24px rgba(0, 0, 0, 0.24);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: #22220e;
      transform: translateY(-4px);
      border-color: rgba(233, 233, 40, 0.52);
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.09),
        0 0 22px rgba(233, 233, 40, 0.14),
        0 12px 24px rgba(0, 0, 0, 0.24);
    }

    &:hover::after {
      animation: ${businessButtonSweep} 3s cubic-bezier(0.22, 1, 0.36, 1);
    }
  }

  ${({ $playIntro }) =>
    $playIntro &&
    css`
      @media (prefers-reduced-motion: no-preference) {
        animation: ${businessButtonGlow} 3.8s ease-in-out 1;

        &::after {
          animation: ${businessButtonSweep} 3.8s cubic-bezier(0.22, 1, 0.36, 1)
            1;
        }
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      display: none;
      animation: none;
    }
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    min-width: 120px;
    min-height: 40px;
    margin-right: 0;
    margin-bottom: 1rem;
  }
`;

const ButtonLabel = styled.span`
  position: relative;
  z-index: 1;
`;

const LangSwitcherWrapper = styled.div`
  @media (max-width: 768px) {
    position: absolute;
    top: -8.4rem;
    left: 1rem;
  }
`;
